import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";

/**
 * ACTIVE PROJECTS — sold and not yet invoiced.
 *
 * The dashboard could tell you what was approved in a PERIOD and what is
 * owed once invoiced, and nothing in between. The value of the projects
 * actually running, accepted at any time, had no home — which made it
 * hard to answer "how much work are we carrying right now".
 *
 * Sits in Live now, so it is not affected by the period filter.
 *
 * INVOICE SENT IS NOT COUNTED HERE, by design: those projects are
 * delivered and billed, and their money is in the AR panel beside this
 * one. The same dollars appearing twice on one screen is how a
 * dashboard stops being believed.
 */
export default function ActiveProjectsCard({
  activeProjects,
}: {
  activeProjects?: {
    value: number;
    projects: number;
    awaitingSchedule: number;
  } | null;
}) {
  // Hooks before the early return, or React sees a different hook count
  // on the render where the field is absent.
  const navigate = useNavigate();

  if (!activeProjects) return null;

  const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

  return (
    <Box
      onClick={() => navigate("/dashboard/queue/active-projects")}
      title="See every project behind this figure"
      sx={{
        flex: "1 1 100%",
        background: themevals.colors.white,
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        borderRadius: "8px",
        padding: "18px 22px",
        cursor: "pointer",
        "&:hover": { borderColor: themevals.colors.dkgrey },
      }}
    >
      <Flex sx={{ alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            fontWeight: 600,
            color: "base_800",
          }}
        >
          Active projects
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            color: themevals.colors.dkgrey,
          }}
        >
          Sold and not yet invoiced
        </Box>
        <Box sx={{ flex: 1 }} />
        {/* Says the card is clickable without a button competing with
            the figure for attention. */}
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            color: themevals.colors.primary,
          }}
        >
          See the projects
        </Box>
      </Flex>

      <Flex
        sx={{
          alignItems: "baseline",
          gap: "14px",
          marginTop: "10px",
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "28px",
            fontWeight: 600,
            color: "base_800",
          }}
        >
          {money(activeProjects.value)}
        </Box>
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "12.5px",
            color: themevals.colors.dkgrey,
          }}
        >
          {/* The count earns its place: $300k across 8 jobs and across
              80 are different operational problems. */}
          {activeProjects.projects === 1
            ? "1 project"
            : activeProjects.projects + " projects"}
          {activeProjects.awaitingSchedule > 0
            ? " \u00b7 " + activeProjects.awaitingSchedule + " not scheduled yet"
            : ""}
        </Box>
      </Flex>
    </Box>
  );
}
